/**
 * The window's own titlebar controls, and the edges that resize it.
 *
 * Drawn only where the app owns the chrome — see `lib/chrome.ts` for what
 * decides that. In a browser `windowChrome` is `null` and none of this is
 * rendered or even reachable: there is nothing for a close button to call.
 *
 * # The grips are not decoration
 *
 * On Wayland an undecorated window has **no pointer resize at all**. GTK3 is
 * always client-side-decorated there and with `decorated = FALSE` it builds no
 * frame and reserves no shadow margin, and KWin adds none of its own to a
 * client that never asked for server-side decoration. So without the eight
 * regions below the window simply cannot be sized, and deleting them as
 * "unused divs" would be a bug nobody could see in a diff.
 *
 * # Why they must not sit inside a drag region
 *
 * A grip is a bare `<div>` with no `tabindex` and no interactive role, and
 * Tauri's drag script only treats `A`, `BUTTON`, `INPUT`, `SELECT`, `TEXTAREA`,
 * `LABEL`, `SUMMARY`, contenteditable, a real `tabindex` and a handful of ARIA
 * roles as blocking. A grip nested under `data-tauri-drag-region` would
 * therefore be walked straight past, and pulling an edge would *move* the
 * window instead of resizing it. They are top-level siblings for that reason
 * and no other.
 */

import { windowChrome } from "@transport";

import * as chrome from "../lib/chrome";
import type { ResizeEdge } from "../lib/net.types";
import * as Icon from "./Icons";

/**
 * `inline` puts the controls in the flow as a flex child instead of pinning
 * them to the window corner.
 *
 * Used by the tab strip, and the reason is alignment rather than taste. Pinned,
 * the controls have to be given a height, and the height of the band they sit
 * in is not a constant -- the strip is content-sized and the header row is
 * 53px, so one number is wrong for the other and the buttons end up a few
 * pixels off from the tab menu beside them. As a flex child under the strip's
 * own `align-items: stretch` they line up by construction, with no number to
 * get wrong and nothing to re-measure when the font changes.
 */
export function WindowControls({ inline = false }: { inline?: boolean }) {
  if (!chrome.drawing.value || !windowChrome) return null;
  const chromeApi = windowChrome;
  const isMax = chrome.maximized.value;

  return (
    <div class={inline ? "win-controls is-inline" : "win-controls"}>
      <button
        class="icon-btn win-btn"
        aria-label="Minimise the window"
        title="Minimise"
        onClick={() => void chromeApi.minimize()}
      >
        <Icon.Minimize />
      </button>
      <button
        class="icon-btn win-btn"
        aria-label={isMax ? "Restore the window" : "Maximise the window"}
        title={isMax ? "Restore" : "Maximise"}
        onClick={() => void chromeApi.toggleMaximize()}
      >
        {isMax ? <Icon.Restore /> : <Icon.Maximize />}
      </button>
      {/*
        Last, and it gets its own hover colour. Close is the one control here
        with no undo, and every desktop puts it at the end of the row -- on this
        machine too: `kwinrc` has no `kdecoration` section, so KDE is on Breeze
        defaults, which is buttons-right with close outermost.
      */}
      <button
        class="icon-btn win-btn win-close"
        aria-label="Close the window"
        title="Close"
        onClick={() => void chromeApi.close()}
      >
        <Icon.Close />
      </button>
    </div>
  );
}

/** The eight regions, and which way each one pulls. */
const EDGES: { edge: ResizeEdge; cls: string }[] = [
  { edge: "north", cls: "n" },
  { edge: "south", cls: "s" },
  { edge: "east", cls: "e" },
  { edge: "west", cls: "w" },
  { edge: "north-west", cls: "nw" },
  { edge: "north-east", cls: "ne" },
  { edge: "south-west", cls: "sw" },
  { edge: "south-east", cls: "se" },
];

export function ResizeGrips() {
  if (!chrome.drawing.value || !windowChrome) return null;
  // Nothing to pull on a window that fills the screen, and a grip lying over
  // the content while maximised is pure loss -- the top one would sit across
  // the search box.
  if (chrome.maximized.value) return null;
  const chromeApi = windowChrome;

  return (
    <>
      {EDGES.map(({ edge, cls }) => (
        <div
          key={edge}
          class={`win-grip win-grip-${cls}`}
          aria-hidden="true"
          // Pointer events rather than mouse, matching `ui/PaneDivider.tsx` and
          // for the same reason it gives: the browser claims a touch gesture
          // before the first move arrives unless it is told not to.
          onPointerDown={(e) => {
            e.preventDefault();
            void chromeApi.startResize(edge);
          }}
        />
      ))}
    </>
  );
}
